import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import externalApiService from '../../../services/externalApiService';

function maskToken(preview) {
  const p = String(preview || '');
  return p ? `••••••${p}` : '—';
}

export default function ExternalApiWebhooksPage() {
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({ open: false, mode: 'create', row: null });
  const [form, setForm] = useState({ integrationId: '', url: '', status: 'active', events: 'message.status' });
  const [secretDialog, setSecretDialog] = useState({ open: false, secret: '' });

  const loadIntegrations = async () => {
    const resp = await externalApiService.listIntegrations().catch(() => null);
    const data = resp?.data?.data || resp?.data || [];
    const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    setIntegrations(list);
  };

  const load = async () => {
    setLoading(true);
    try {
      const resp = await externalApiService.listWebhooks();
      const data = resp?.data?.data || resp?.data || [];
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setRows(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegrations().catch(() => {});
    load().catch(() => {});
  }, []);

  const openCreate = () => {
    setEdit({ open: true, mode: 'create', row: null });
    setForm({ integrationId: '', url: '', status: 'active', events: 'message.status' });
  };

  const openEdit = (row) => {
    setEdit({ open: true, mode: 'edit', row });
    setForm({
      integrationId: String(row?.integrationId || ''),
      url: row?.url || '',
      status: row?.status || 'active',
      events: Array.isArray(row?.events) ? row.events.join(',') : 'message.status'
    });
  };

  const closeEdit = () => setEdit({ open: false, mode: 'create', row: null });

  const save = async () => {
    setLoading(true);
    try {
      const events = String(form.events || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (edit.mode === 'create') {
        const resp = await externalApiService.createWebhook({
          integrationId: Number(form.integrationId),
          url: form.url,
          status: form.status,
          events: events.length ? events : ['message.status']
        });
        const secret = resp?.data?.data?.secret || resp?.data?.secret;
        if (secret) setSecretDialog({ open: true, secret });
      } else {
        await externalApiService.updateWebhook(edit.row.id, {
          url: form.url,
          status: form.status,
          events: events.length ? events : ['message.status']
        });
      }
      closeEdit();
      await load();
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async (row) => {
    setLoading(true);
    try {
      const resp = await externalApiService.regenerateWebhookSecret(row.id);
      const secret = resp?.data?.data?.secret || resp?.data?.secret;
      if (secret) setSecretDialog({ open: true, secret });
      await load();
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: 'integrationId', label: 'Integração', render: (v) => {
      const found = integrations.find(i => String(i.id) === String(v));
      return found?.name || String(v || '—');
    }},
    { key: 'url', label: 'URL' },
    { key: 'events', label: 'Eventos', render: (v) => Array.isArray(v) ? v.join(', ') : '—' },
    { key: 'status', label: 'Status', render: (v) => <Chip size="small" color={v === 'active' ? 'success' : 'default'} label={v === 'active' ? 'Ativo' : 'Inativo'} /> },
    { key: 'secretPreview', label: 'Secret', render: (v) => maskToken(v) },
    {
      key: 'actions',
      label: 'Ações',
      align: 'right',
      render: (_, r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={() => openEdit(r)}>Editar</Button>
          <Button size="small" variant="contained" onClick={() => regenerate(r)}>Regenerar secret</Button>
        </Stack>
      )
    }
  ];

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Webhooks (Integrações ERP)</Typography>
        <Button variant="contained" onClick={openCreate}>Criar webhook</Button>
      </Box>

      {loading ? (
        <LoadingState label="Carregando webhooks..." />
      ) : rows.length ? (
        <DataTable columns={columns} rows={rows} getKey={(r) => r.id} />
      ) : (
        <Paper>
          <EmptyState title="Nenhum webhook cadastrado" actionLabel="Criar webhook" onAction={openCreate} />
        </Paper>
      )}

      <Dialog open={edit.open} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{edit.mode === 'create' ? 'Criar webhook' : 'Editar webhook'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <TextField
              select
              label="Integração"
              value={form.integrationId}
              onChange={(e) => setForm(prev => ({ ...prev, integrationId: e.target.value }))}
              disabled={edit.mode !== 'create'}
              fullWidth
            >
              <MenuItem value="">Selecione</MenuItem>
              {integrations.map(i => (
                <MenuItem key={i.id} value={String(i.id)}>{i.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="URL"
              value={form.url}
              onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Eventos (separados por vírgula)"
              value={form.events}
              onChange={(e) => setForm(prev => ({ ...prev, events: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
              fullWidth
            >
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="inactive">Inativo</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form.url || !form.integrationId || loading}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={secretDialog.open} onClose={() => setSecretDialog({ open: false, secret: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Secret do webhook</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Este secret é exibido apenas agora. Armazene com segurança.
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
              {secretDialog.secret}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecretDialog({ open: false, secret: '' })}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

